import {isTouchable} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {ButtonHTMLAttributes} from 'react';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

type ButtonVariant = 'primary' | 'ghost';

export interface ButtonProps extends RequiredTestIdProps {
  title: string;
  variant?: ButtonVariant;
  isDisabled?: boolean;
  isActive?: boolean;
  isFullWidth?: boolean;
  justifyContent?: 'start' | 'center' | 'end';
  size?: 'small' | 'default';
  tabIndex?: number;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}

const buttonVariants = cva(
  'flex shrink-0 select-none items-center rounded-[var(--radius-small)] border-none transition-opacity duration-300 ease-out',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--color-palettes-black-900-70)]',
        ghost: 'bg-transparent',
      },
      size: {
        small: 'h-6 px-1',
        default: 'h-9 px-2',
      },
      isActive: {
        true: 'text-[var(--color-white)]',
        false: 'text-[rgba(255,255,255,0.6)]',
      },
      isFullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
      justifyContent: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
      },
      isDisabled: {
        true: 'pointer-events-none cursor-not-allowed opacity-20',
        false: '[pointer-events:all] cursor-pointer opacity-100',
      },
      isHoverEnabled: {
        true: 'hover:text-[var(--color-white)]',
        false: '',
      },
    },
  }
);

export function Button(props: ButtonProps) {
  const isTouchableDevice = isTouchable();

  return (
    <button
      data-testid={suffixTestId('button', props)}
      onClick={props.onClick}
      disabled={props.isDisabled}
      type="button"
      tabIndex={props.tabIndex}
      className={cn(
        buttonVariants({
          variant: props.variant ?? 'primary',
          size: props.size ?? 'default',
          isActive: Boolean(props.isActive),
          isFullWidth: Boolean(props.isFullWidth),
          justifyContent: props.justifyContent ?? 'center',
          isDisabled: Boolean(props.isDisabled),
          isHoverEnabled: !isTouchableDevice,
        })
      )}
    >
      {props.title}
    </button>
  );
}
